import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import * as L from 'leaflet';
import { DeliveryTrackPosition, isValidLatLng, LatLng } from '../delivery-tracking.models';
import { updatedAgo } from '../delivery-tracking.view';
import { bearing } from '../geo';
import { mapConfig } from '../map-config';

const STORE_ICON =
  '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" ' +
  'stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 9l1.5-5h15L21 9"/>' +
  '<path d="M4 9v11h16V9"/><path d="M9 20v-6h6v6"/></svg>';
const HOME_ICON =
  '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" ' +
  'stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 11l9-8 9 8"/>' +
  '<path d="M5 10v10h14V10"/></svg>';
const DRIVER_ICON =
  '<svg viewBox="-14 -16 28 30" width="30" height="32" aria-hidden="true">' +
  '<path d="M0 -13 L9 9 L0 4 L-9 9 Z" fill="#0f172a" stroke="#ffffff" stroke-width="2" stroke-linejoin="round"/></svg>';

/**
 * Live map for a delivery: store pin, destination pin and the driver marker.
 * Leaflet runs outside Angular's zone; the component still works as a plain
 * panel with pins when the tile server cannot be reached.
 */
@Component({
  selector: 'app-delivery-map',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './delivery-map.component.html',
  styleUrls: ['./delivery-map.component.scss'],
  // Leaflet creates its own DOM, which emulated encapsulation would not reach.
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DeliveryMapComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input() store: Partial<LatLng> | null = null;
  @Input() destination: Partial<LatLng> | null = null;
  @Input() position: DeliveryTrackPosition | null = null;
  /** Driver first name only, used for the accessible description. */
  @Input() driverName: string | null = null;
  /** Emitted once when the map cannot be created at all. */
  @Output() unavailable = new EventEmitter<void>();

  @ViewChild('canvas', { static: true }) canvas: ElementRef<HTMLDivElement>;

  updatedText = '';
  userMoved = false;
  tilesFailed = false;

  private map: L.Map | null = null;
  private storeMarker: L.Marker | null = null;
  private destinationMarker: L.Marker | null = null;
  private driverMarker: L.Marker | null = null;
  private line: L.Polyline | null = null;
  private frame: number | null = null;
  private ticker: any = null;
  private resizeTimer: any = null;
  private programmatic = false;
  private rotation = 0;
  private lastMoveAt = 0;
  private tilesLoaded = 0;

  constructor(private zone: NgZone, private cdr: ChangeDetectorRef) {}

  get ariaLabel(): string {
    const who = this.driverName ? `${this.driverName}, your driver` : 'your driver';
    return `Map showing the store, your delivery address and ${who}`;
  }

  ngAfterViewInit(): void {
    this.zone.runOutsideAngular(() => this.create());
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.map) {
      return;
    }
    this.zone.runOutsideAngular(() => {
      if (changes.store || changes.destination) {
        this.placePins();
        this.fit();
      }
      if (changes.position) {
        this.moveDriver();
      }
    });
    this.refreshUpdatedText();
  }

  ngOnDestroy(): void {
    this.cancelFrame();
    clearInterval(this.ticker);
    clearTimeout(this.resizeTimer);
    if (this.map) {
      this.map.remove();
      this.map = null;
    }
  }

  recenter(): void {
    this.userMoved = false;
    this.zone.runOutsideAngular(() => this.fit());
  }

  private create(): void {
    try {
      const config = mapConfig();
      const map = L.map(this.canvas.nativeElement, {
        zoomControl: true,
        scrollWheelZoom: false, // the page scrolls, the map does not hijack it
        dragging: !L.Browser.mobile, // one finger scrolls the page; pinch still zooms
        zoomSnap: 0.5,
        maxZoom: 18,
        minZoom: 3,
      });
      this.map = map;
      map.attributionControl.setPrefix(false);

      const tiles = L.tileLayer(config.tileUrl, {
        attribution: config.attribution,
        subdomains: 'abcd',
        maxZoom: 19,
      });
      tiles.on('tileload', () => {
        this.tilesLoaded++;
        if (this.tilesFailed) {
          this.setTilesFailed(false);
        }
      });
      tiles.on('tileerror', () => {
        if (this.tilesLoaded === 0 && !this.tilesFailed) {
          this.setTilesFailed(true);
        }
      });
      tiles.addTo(map);

      map.on('dragstart', () => this.markUserMoved());
      map.on('zoomstart', () => {
        if (!this.programmatic) {
          this.markUserMoved();
        }
      });

      // The view must be set first: Leaflet only builds marker elements on a ready map.
      this.fit();
      this.placePins();
      this.moveDriver(true);

      // The container may get its final size after first paint.
      this.resizeTimer = setTimeout(() => {
        if (this.map) {
          this.map.invalidateSize();
          this.fit();
        }
      }, 150);

      this.ticker = setInterval(() => this.refreshUpdatedText(), 1000);
      this.refreshUpdatedText();
    } catch (error) {
      if (this.map) {
        try {
          this.map.remove();
        } catch (e) {
          /* nothing to clean up */
        }
        this.map = null;
      }
      this.zone.run(() => this.unavailable.emit());
    }
  }

  private placePins(): void {
    if (!this.map) {
      return;
    }
    this.storeMarker = this.placePin(this.storeMarker, this.store, 'dt-pin dt-pin--store', STORE_ICON, 'Store');
    this.destinationMarker = this.placePin(
      this.destinationMarker,
      this.destination,
      'dt-pin dt-pin--home',
      HOME_ICON,
      'You'
    );
  }

  private placePin(
    marker: L.Marker | null,
    point: Partial<LatLng> | null,
    className: string,
    icon: string,
    label: string
  ): L.Marker | null {
    if (!isValidLatLng(point)) {
      if (marker) {
        marker.remove();
      }
      return null;
    }
    if (marker) {
      marker.setLatLng([point.lat, point.lng]);
      return marker;
    }
    return L.marker([point.lat, point.lng], {
      icon: L.divIcon({
        className,
        // Fixed strings only: nothing from the network is written as HTML.
        html: `<span class="dt-pin__dot">${icon}</span><span class="dt-pin__label">${label}</span>`,
        iconSize: [32, 32],
        iconAnchor: [16, 16],
      }),
      interactive: false,
      keyboard: false,
    }).addTo(this.map);
  }

  private moveDriver(immediate = false): void {
    if (!this.map) {
      return;
    }
    const target = this.position;
    if (!isValidLatLng(target)) {
      this.cancelFrame();
      if (this.driverMarker) {
        this.driverMarker.remove();
        this.driverMarker = null;
      }
      if (this.line) {
        this.line.remove();
        this.line = null;
      }
      return;
    }

    const to = L.latLng(target.lat, target.lng);
    if (!this.driverMarker) {
      this.driverMarker = L.marker(to, {
        icon: L.divIcon({
          className: 'dt-driver',
          html: `<span class="dt-driver__arrow">${DRIVER_ICON}</span>`,
          iconSize: [36, 36],
          iconAnchor: [18, 18],
        }),
        interactive: false,
        keyboard: false,
        zIndexOffset: 1000,
      }).addTo(this.map);
      if (isValidLatLng(this.destination)) {
        this.line = L.polyline([to, [this.destination.lat, this.destination.lng]], {
          color: '#0666ff',
          weight: 3,
          opacity: 0.55,
          dashArray: '2 8',
          lineCap: 'round',
          interactive: false,
        }).addTo(this.map);
      }
      this.rotate(target.heading, null, to);
      this.lastMoveAt = Date.now();
      return;
    }

    const from = this.driverMarker.getLatLng();
    this.rotate(target.heading, from, to);
    const sinceLast = Date.now() - this.lastMoveAt;
    this.lastMoveAt = Date.now();

    if (immediate || this.prefersReducedMotion() || from.equals(to)) {
      this.cancelFrame();
      this.setDriver(to);
    } else {
      // Glide for as long as the gap between updates, within sensible limits.
      this.glide(from, to, Math.min(2000, Math.max(400, sinceLast)));
    }
    this.keepDriverInView(to);
  }

  private glide(from: L.LatLng, to: L.LatLng, duration: number): void {
    this.cancelFrame();
    const started = performance.now();
    const step = (now: number) => {
      const t = Math.min(1, (now - started) / duration);
      this.setDriver(L.latLng(from.lat + (to.lat - from.lat) * t, from.lng + (to.lng - from.lng) * t));
      this.frame = t < 1 ? requestAnimationFrame(step) : null;
    };
    this.frame = requestAnimationFrame(step);
  }

  private setDriver(at: L.LatLng): void {
    if (this.driverMarker) {
      this.driverMarker.setLatLng(at);
    }
    if (this.line && isValidLatLng(this.destination)) {
      this.line.setLatLngs([at, [this.destination.lat, this.destination.lng]]);
    }
  }

  private rotate(heading: number | null | undefined, from: L.LatLng | null, to: L.LatLng): void {
    let target: number | null = typeof heading === 'number' && isFinite(heading) ? heading : null;
    if (target === null && from && !from.equals(to)) {
      target = bearing(from, to);
    }
    if (target === null || !this.driverMarker) {
      return;
    }
    // Turn the short way round instead of spinning through 0°.
    const delta = ((((target - this.rotation) % 360) + 540) % 360) - 180;
    this.rotation += delta;
    const element = this.driverMarker.getElement();
    const arrow = element ? (element.querySelector('.dt-driver__arrow') as HTMLElement | null) : null;
    if (arrow) {
      arrow.style.transform = `rotate(${this.rotation}deg)`;
    }
  }

  private keepDriverInView(at: L.LatLng): void {
    if (!this.map || this.userMoved) {
      return;
    }
    if (!this.map.getBounds().pad(-0.12).contains(at)) {
      this.fit();
    }
  }

  private fit(): void {
    if (!this.map || this.userMoved) {
      return;
    }
    const points: L.LatLngTuple[] = [];
    [this.store, this.destination, this.position].forEach((point) => {
      if (isValidLatLng(point)) {
        points.push([point.lat, point.lng]);
      }
    });
    if (!points.length) {
      return;
    }
    this.programmatic = true;
    try {
      if (points.length === 1) {
        this.map.setView(points[0], 15, { animate: false });
      } else {
        this.map.fitBounds(L.latLngBounds(points), { padding: [40, 40], maxZoom: 16, animate: false });
      }
    } finally {
      this.programmatic = false;
    }
  }

  private markUserMoved(): void {
    if (!this.userMoved) {
      this.zone.run(() => {
        this.userMoved = true;
        this.cdr.markForCheck();
      });
    }
  }

  private setTilesFailed(failed: boolean): void {
    this.zone.run(() => {
      this.tilesFailed = failed;
      this.cdr.markForCheck();
    });
  }

  private refreshUpdatedText(): void {
    const text = updatedAgo(this.position ? this.position.recorded_at : null);
    if (text !== this.updatedText) {
      this.zone.run(() => {
        this.updatedText = text;
        this.cdr.markForCheck();
      });
    }
  }

  private prefersReducedMotion(): boolean {
    return typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  private cancelFrame(): void {
    if (this.frame !== null) {
      cancelAnimationFrame(this.frame);
      this.frame = null;
    }
  }
}
